import { gql, useLazyQuery } from '@apollo/client';
import { useEffect, useRef } from 'react';
import {
  CriteriaSuggestionDropdownValuesQuery,
  CriteriaSuggestionDropdownValuesQueryVariables,
  EntityTypeEnum
} from '../../../gql/typings';
import { Optional } from '../../util/StateArrayType';

type UseSuggestionDropdownValuesArgs = {
  search: string;
  service: string;
  criteria: {
    id: number;
    entityType: EntityTypeEnum;
  };
  criteriaPathId: number;
  countryCodes?: Optional<string[]>;

  selectedKeys: string[];
};

export type CriteriaSuggestionReturned = {
  records: CriteriaSuggestionRecord[];
  totalCount: Optional<number>;
  searchCount: Optional<number>;
  hasMoreResults: boolean;
  triggerLoading: () => void;
  fetchMore: () => void;
  loading: boolean;
};

export type CriteriaSuggestionRecord = NonNullable<CriteriaSuggestionDropdownValuesQuery['suggestions']['nodes']['0']>;

const PER_LOAD = 100;

const buildCriteria = (args: UseSuggestionDropdownValuesArgs, offset: number) => ({
  limit: PER_LOAD,
  search: `%${args.search}%`,
  service: args.service,
  criteriaPathId: args.criteriaPathId,
  countryCodes: args.countryCodes,
  offset,
});

export const useSuggestionDropdownValues = (args: UseSuggestionDropdownValuesArgs): CriteriaSuggestionReturned => {
  const totalCount = useRef<number>();

  const [load, {
    data,
    loading,
    refetch,
    fetchMore,
  }] = useLazyQuery<CriteriaSuggestionDropdownValuesQuery, CriteriaSuggestionDropdownValuesQueryVariables>(DATA_QUERY);

  useEffect(() => {
    refetch?.(buildCriteria(args, 0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [args.search, refetch]);

  // Set the total count
  if (!totalCount.current && data?.suggestions.totalCount) totalCount.current = data.suggestions.totalCount;

  return {
    records: data?.suggestions.nodes ?? [],
    totalCount: totalCount.current,
    searchCount: data?.suggestions.totalCount,
    hasMoreResults: data ? (data.suggestions.totalCount > data.suggestions.nodes.length) : false,
    triggerLoading: () => load({ variables: buildCriteria(args, 0) }),
    loading,
    fetchMore: () => {
      if (fetchMore) fetchMore({
        variables: buildCriteria(args, data!.suggestions.nodes.length),
      });
    },
  };
};


const DATA_QUERY = gql`
  query CriteriaSuggestionDropdownValues(
    $countryCodes: [CountryCode!],
    $service: String!,
    $criteriaPathId: Int!,
    $offset: Int!,
    $limit: Int!,
    $search: String
  ) {
    suggestions(
      countryCodes: $countryCodes,
      service: $service,
      criteriaPathId: $criteriaPathId,
      includeInactive: false,
      includeUnplaced: false,
      includeOptedOut: false,
      search: $search,
      fetchSize: { offset: $offset, limit: $limit },
    ) {
      hash
      totalCount
      search
      service
      countryCodes
      nodes {
        key
        parentKey
        isValueHeadingKey
        isGlobalOnly
        value
        valueGb
        cacheKey
      }
    }
  }
`;
